import { cloneElement, type CSSProperties, type ReactElement } from 'react';
import styled from 'styled-components';

import { cn } from '@/lib/cn';

import { Flex } from '../base';
import TooltipV2 from '../Tooltip/TooltipV2';
import type { TabRadioSizeType } from './types';

type Props<T extends StringNumberValue> = {
  options: TabRadioOptionT<T>[];
  value: T;
  onChange: (key: T) => void;
  sizeType?: TabRadioSizeType;
  style?: CSSProperties;
};

export type TabRadioOptionT<T extends StringNumberValue> = {
  node: ReactElement;
  tooltip?: string;
  key: T;
  className?: string;
  disabled?: boolean;
};

const TabRadio = <T extends StringNumberValue>(props: Props<T>) => {
  const { sizeType = 'l' } = props;
  return (
    <Style style={{ ...props.style }} sizeType={sizeType}>
      {props.options.map((item) => (
        <ItemStyle
          key={item.key}
          isSelected={props.value === item.key}
          onClick={() => {
            if (!item.disabled) {
              props.onChange(item.key);
            }
          }}
          sizeType={sizeType}
          className={item.className}
          disabled={item.disabled}
        >
          {item.tooltip ? (
            <TooltipV2 tooltipNode={item.tooltip}>{item.node}</TooltipV2>
          ) : (
            item.node
          )}
        </ItemStyle>
      ))}
    </Style>
  );
};

const Style = styled(Flex)<{ sizeType: TabRadioSizeType }>`
  background-color: ${(props) => props.theme.colors.gray150};
  border-radius: ${(props) => props.theme.radius.token.lg}px;
  padding: 4px;
  gap: 4px;
  height: ${({ sizeType }) => {
    switch (sizeType) {
      case 'm':
        return '32px';
      case 'l':
        return '36px';
    }
  }};
`;

const ItemStyle = (props: {
  isSelected: boolean;
  sizeType: TabRadioSizeType;
  onClick: (e: React.MouseEvent<HTMLDivElement>) => void;
  children: ReactElement;
  className?: string;
  disabled?: boolean;
}) => {
  const clonedNode = cloneElement(props.children, {
    onClick: props.onClick,
    className: cn(
      'flex items-center justify-center h-full transition-all duration-200 gap-2 rounded-sm text-gray-600 border border-transparent bg-transparent',
      props.disabled
        ? 'cursor-not-allowed opacity-50'
        : 'cursor-pointer hover:bg-gray-900/5',
      props.sizeType === 'm' && 'py-0 px-3 text-xs',
      props.sizeType === 'l' && 'py-0 px-3.5 text-sm',
      props.isSelected &&
        'border-gray-400 text-gray-900 bg-gray-100 hover:bg-gray-100 hover:border-primary-100 hover:text-primary-100',
      props.className,
    ),
  });
  return clonedNode;
};

export default TabRadio;
